/* Hallmark · component: bento-cell (Regions) · genre: editorial · theme: Garden
 * (project palette, preserved) · pre-emit critique: P4 H5 E4 S5 R4 V5
 * cell: drifting-label field — names scatter over the whole card and bounce off
 * the walls, each other, and the city name; no bands, no underline
 */
import { useMemo, useRef } from 'react';
import { useReducedMotion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Reveal } from '@/components/ui/reveal';
import { cn } from '@/lib/utils';
import { CtaButton } from './cta-button';
import { scatterPositions, useFloatingNames } from './use-floating-names';

type City = {
  name: string;
  /** Real localities we cover, drifting across the card. */
  areas: string[];
  /** Anchor cities get a 2×2 cell on desktop and a full-width one on phones. */
  big?: boolean;
  /** Per-city accent, all registered tokens. */
  tint: { wash: string; border: string };
};

// Order matters: grid auto-placement turns this into the bento. Hyderabad takes
// rows 1-2 on the left, Amaravati and Vizag fill the top right, Bangalore drops
// into rows 2-3 beside them, and the last two close out the bottom left.
const CITIES: City[] = [
  {
    name: 'Hyderabad',
    big: true,
    tint: { wash: 'bg-ring/5', border: 'hover:border-ring/40' },
    areas: [
      'Gachibowli',
      'Kokapet',
      'Kondapur',
      'Madhapur',
      'Shamshabad',
      'Adibatla',
      'Narsingi',
      'Tellapur',
      'Manikonda',
      'Miyapur',
      'Bachupally',
      'Kompally',
      'Shankarpally',
      'Ghatkesar',
      'Patancheru',
      'Uppal',
      'Nizampet',
      'Sangareddy',
      'Medchal',
      'Mokila',
      'Pocharam',
      'Bhongir',
      'Shadnagar',
      'Secunderabad',
    ],
  },
  {
    name: 'Amaravati',
    tint: { wash: 'bg-color-4/10', border: 'hover:border-color-4/50' },
    areas: ['Thullur', 'Mandadam', 'Velagapudi', 'Rayapudi', 'Undavalli', 'Venkatapalem'],
  },
  {
    name: 'Vizag',
    tint: { wash: 'bg-color-3/10', border: 'hover:border-color-3/50' },
    areas: ['Madhurawada', 'Bheemili', 'Anandapuram', 'Rushikonda', 'Pendurthi', 'Anakapalle'],
  },
  {
    name: 'Bangalore',
    big: true,
    tint: { wash: 'bg-color-5/10', border: 'hover:border-color-5/60' },
    areas: [
      'Whitefield',
      'Sarjapur Road',
      'Devanahalli',
      'Electronic City',
      'Yelahanka',
      'Hennur',
      'Kanakapura Road',
      'Hoskote',
      'Attibele',
      'Jigani',
      'Varthur',
      'Anekal',
      'Nelamangala',
      'Doddaballapur',
      'Bannerghatta',
      'Chandapura',
      'Budigere',
      'Kengeri',
      'Chikkaballapur',
      'Magadi Road',
      'Sarjapur',
      'Malur',
    ],
  },
  {
    name: 'Vijayawada',
    tint: { wash: 'bg-color-2/10', border: 'hover:border-color-2/40' },
    areas: ['Gannavaram', 'Penamaluru', 'Kankipadu', 'Poranki', 'Gollapudi', 'Ibrahimpatnam'],
  },
  {
    name: 'Guntur',
    tint: { wash: 'bg-sidebar-primary/10', border: 'hover:border-sidebar-primary/50' },
    areas: ['Mangalagiri', 'Tadepalli', 'Tenali', 'Pedakakani', 'Nallapadu', 'Ponnur'],
  },
];

function CityCell({ name, areas, big, tint }: City) {
  const cellRef = useRef<HTMLLIElement>(null);
  const reduceMotion = useReducedMotion();

  // Anchor cells are roughly 2:1, the rest roughly square — the aspect steers
  // how many columns the stratified grid uses.
  const points = useMemo(
    () => scatterPositions(areas.length, name, big ? 2 : 1.1),
    [areas.length, name, big],
  );

  useFloatingNames(cellRef, { enabled: !reduceMotion, seedKey: name });

  return (
    <li
      ref={cellRef}
      className={cn(
        'group relative isolate overflow-hidden rounded-2xl border border-border transition-[transform,border-color,box-shadow] duration-300 hover:shadow-lg motion-safe:hover:-translate-y-1',
        tint.wash,
        tint.border,
        big
          ? 'col-span-2 min-h-[260px] sm:min-h-[300px] md:row-span-2 md:min-h-[360px]'
          : 'min-h-[170px] sm:min-h-[190px]',
      )}
    >
      {/* Decorative: the drifting field is aria-hidden and the same list is read
          out once, in order, by the sr-only sentence below. */}
      {/* Anchor cells thin to every other label below sm: 22 labels in a 343px
          cell is ~33% area coverage, which leaves the solver permanently in
          contact instead of drifting. The hook skips the hidden ones. */}
      <div
        aria-hidden="true"
        className={cn(
          'absolute inset-0',
          // Phones can't hold the full list: an anchor cell is 343px wide and a
          // small one only 166px, against labels like "Venkatapalem" at ~100px.
          // Anchors drop to every other name, small cells to two thirds.
          big ? 'max-sm:[&>span:nth-child(even)]:hidden' : 'max-sm:[&>span:nth-child(3n)]:hidden',
        )}
      >
        {areas.map((area, i) => (
          <span
            key={area}
            data-float
            /* `transform` centres the label on its point; the physics loop owns
               the separate `translate` property, so the two never fight. */
            className={cn(
              'absolute select-none whitespace-nowrap leading-none text-foreground/55 transition-colors duration-300 [transform:translate(-50%,-50%)] group-hover:text-foreground/85',
              big ? 'text-xs sm:text-sm' : 'text-[10px] sm:text-xs',
            )}
            style={{ left: `${points[i].x}%`, top: `${points[i].y}%` }}
          >
            {area}
          </span>
        ))}
      </div>

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <h3
          data-anchor
          className={cn(
            'whitespace-nowrap font-display font-medium tracking-tight text-foreground',
            big ? 'text-4xl md:text-5xl' : 'text-lg sm:text-xl md:text-2xl',
          )}
        >
          {name}
        </h3>
      </div>

      <span className="sr-only">{`${name} — covering ${areas.join(', ')}.`}</span>
    </li>
  );
}

/**
 * Coverage as a bento grid: two anchor cities on 2×2 cells, four on single
 * cells. Each cell is a field of drifting locality names orbiting the city,
 * which sits immovable in the middle — see `use-floating-names.ts`.
 */
export function Regions() {
  return (
    <section id="coverage" className="scroll-mt-8 border-y border-line-strong bg-card/60">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 md:py-28">
        <Reveal className="max-w-2xl">
          <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-foreground [overflow-wrap:anywhere] md:text-4xl">
            Where we operate.
          </h2>
          <p className="mt-5 leading-relaxed text-foreground/65">
            We currently serve these regions and their surroundings in Telangana, Karnataka and AP.
          </p>
        </Reveal>

        {/* One Reveal around the whole grid rather than one per cell: a per-cell
            wrapper would either become the grid item (breaking the span classes)
            or need `display:contents`, which silently kills the transform the
            animation relies on. */}
        <Reveal>
          <ul className="mt-10 grid grid-cols-2 gap-3 sm:mt-14 sm:gap-4 md:grid-cols-4">
            {CITIES.map((city) => (
              <CityCell key={city.name} {...city} />
            ))}
          </ul>
        </Reveal>

        <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-foreground/65">
          <p>and many other areas.</p>
          <CtaButton location="regions" variant="quiet" size="sm">
            Sign up to see all areas
            <ArrowRight className="size-4" />
          </CtaButton>
        </div>
      </div>
    </section>
  );
}
